import { Input, Popconfirm, Typography } from "antd";
import { DownOutlined, WarningTwoTone } from "@ant-design/icons";
import { FontAwesomeIcon, Icon } from "assets/icons";
import CustomTable from "components/CustomTable";
import { LoyaltySection } from "features/LoyaltyPackage/presentation/components";
import { LOYALTY_APPLY_SCOPE } from "features/LoyaltyPackage/core/constants";
import { formatProductMilestoneSummary, getEffectiveApplyScope } from "features/LoyaltyPackage/core/utils";
import { t } from "i18next";
import { useState } from "react";
import { Link } from "react-router-dom";
import { notify } from "utils/helperFuncs";
import MilestoneConfigModal from "./MilestoneConfigModal";
import ProductSelectModal from "./ProductSelectModal";

const { Text } = Typography;

function ProductTable({
  className,
  products,
  milestones,
  applyScope,
  categoryId,
  productError,
  onAddProducts,
  onRemoveProduct,
  onUpdateProductMilestones,
}) {
  const [selectModalVisible, setSelectModalVisible] = useState(false);
  const [configProduct, setConfigProduct] = useState(null);

  const effectiveApplyScope = getEffectiveApplyScope(applyScope);
  const canAddMore = effectiveApplyScope !== LOYALTY_APPLY_SCOPE.SINGLE_PRODUCT || products.length < 1;
  const canSelectProduct = Boolean(categoryId) && canAddMore;

  const openSelectModal = () => {
    if (!categoryId) {
      notify.error({ message: t("loyaltyPackage.packageForm.selectCategoryFirst") });
      return;
    }
    if (!canAddMore) {
      notify.error({ message: t("loyaltyPackage.packageForm.singleProductLimit") });
      return;
    }
    setSelectModalVisible(true);
  };

  const indexWidth = 72;
  const codeWidth = 160;
  const statusWidth = 100;
  const productColWidth = `calc((100% - ${indexWidth + codeWidth + statusWidth}px) / 2)`;

  const columns = [
    {
      title: t("common.index"),
      key: "index",
      width: indexWidth,
      align: "center",
      className: "package-form-product-index-col",
      onCell: () => ({ style: { verticalAlign: "middle" } }),
      render: (_, __, index) => <span className="package-form-product-index">{index + 1}</span>,
    },
    {
      title: t("loyaltyPackage.packageForm.productCode"),
      key: "productCode",
      width: codeWidth,
      align: "center",
      className: "package-form-product-code-col",
      onCell: () => ({ style: { verticalAlign: "middle" } }),
      render: (_, record) => {
        if (!record.productCode) return "—";
        return (
          <Link to={`/product/detail/${record.productId}`} target="_blank" className="loyalty-package-link">
            {record.productCode}
          </Link>
        );
      },
    },
    {
      title: t("loyaltyPackage.packageForm.appliedProductColumn"),
      key: "productName",
      width: productColWidth,
      className: "package-form-product-name-col",
      onCell: () => ({ style: { verticalAlign: "top", maxWidth: 0 } }),
      render: (_, record) => <Input readOnly value={record.productName} className="package-form-product-name-input" />,
    },
    {
      title: t("loyaltyPackage.packageForm.discountPerProduct"),
      key: "milestones",
      width: productColWidth,
      className: "package-form-product-discount-col",
      onCell: () => ({ style: { verticalAlign: "top", maxWidth: 0 } }),
      render: (_, record) => {
        const summary = formatProductMilestoneSummary(record, milestones);
        const hasSummary = summary.length > 0;

        return (
          <div className="package-form-product-discount-cell">
            <div className="package-form-product-discount-lines">
              {hasSummary ? (
                summary.map((line, idx) => (
                  <span key={idx} className="package-form-product-discount-item">
                    {line}
                  </span>
                ))
              ) : (
                <span className="package-form-product-discount-item--empty">&nbsp;</span>
              )}
            </div>
            <div className="package-form-product-discount-hint">
              {hasSummary ? (
                <>
                  <Text type="secondary">
                    {t("loyaltyPackage.packageForm.milestoneConfiguredHint", { count: summary.length })}
                  </Text>
                  <Text type="secondary"> | </Text>
                </>
              ) : (
                <>
                  <Text type="secondary">{t("loyaltyPackage.packageForm.noDiscountLevel")}</Text>
                  <Text type="secondary"> | </Text>
                </>
              )}
              <span className="package-form-product-discount-link" onClick={() => setConfigProduct(record)}>
                {t("loyaltyPackage.packageForm.viewDetail")}
              </span>
            </div>
          </div>
        );
      },
    },
    {
      title: t("loyaltyPackage.package.status"),
      key: "status",
      width: statusWidth,
      align: "center",
      className: "package-form-product-status-col",
      onCell: () => ({ style: { verticalAlign: "middle" } }),
      render: (_, record) => (
        <div className="package-form-product-status-cell">
          <Popconfirm
            title={t("loyaltyPackage.packageForm.confirmRemoveProductMessage")}
            okText={t("common.confirm")}
            cancelText={t("loyaltyPackage.form.cancel")}
            icon={<WarningTwoTone twoToneColor="#faad14" />}
            placement="topRight"
            align={{ offset: [20, -4] }}
            overlayClassName="package-form-product-delete-popconfirm"
            onConfirm={() => onRemoveProduct(record.productId)}
          >
            <span className="package-form-delete-action">
              <FontAwesomeIcon icon={Icon.faTrash} className="package-form-delete-icon" />
            </span>
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <LoyaltySection
      className={className}
      title={t("loyaltyPackage.form.productSection")}
      bodyClassName="custom-table-list-loyalty"
    >
      <div
        role="button"
        tabIndex={canSelectProduct ? 0 : -1}
        className={`package-form-product-select-trigger${
          canSelectProduct
            ? " package-form-product-select-trigger--enabled"
            : " package-form-product-select-trigger--disabled"
        }`}
        onClick={openSelectModal}
        onKeyDown={(event) => {
          if (!canSelectProduct) return;
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            openSelectModal();
          }
        }}
      >
        <span className="package-form-product-select-label">{t("loyaltyPackage.product.selectProduct")}</span>
        <DownOutlined className="package-form-product-select-arrow" />
      </div>

      {productError ? <div className="package-form-product-error">{productError}</div> : null}

      {products.length > 0 ? (
        <CustomTable
          rowKey="productId"
          dataSource={products}
          columns={columns}
          pagination={false}
          tableLayout="fixed"
          className="package-form-product-table"
        />
      ) : (
        <Text type="secondary" className="package-form-product-empty">
          {t("loyaltyPackage.packageForm.productEmptyHint")}
        </Text>
      )}

      {selectModalVisible ? (
        <ProductSelectModal
          visible
          categoryId={categoryId}
          applyScope={effectiveApplyScope}
          selectedProductIds={products.map((p) => p.productId)}
          onCancel={() => setSelectModalVisible(false)}
          onConfirm={(selected) => {
            onAddProducts(selected);
            setSelectModalVisible(false);
          }}
        />
      ) : null}

      <MilestoneConfigModal
        visible={Boolean(configProduct)}
        product={configProduct}
        milestones={milestones}
        onCancel={() => setConfigProduct(null)}
        onSave={onUpdateProductMilestones}
      />
    </LoyaltySection>
  );
}

export default ProductTable;
